import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { EstablishmentService } from '../../services/establishment/establishment.service';
import { ToastService } from '../../services/toast/toast.service';
import { PermissionService } from '../../services/permission/permission.service';
import { LoaderComponent } from '../../atoms/loader/loader.component';
import { PropertyFormDialogComponent } from './property-form-dialog/property-form-dialog.component';
import { RoomFormDialogComponent } from './room-form-dialog/room-form-dialog.component';
import { AssignUtsavDialogComponent } from './assign-utsav-dialog/assign-utsav-dialog.component';
import { SelectUtsavDialogComponent } from './select-utsav-dialog/select-utsav-dialog.component';
import { RoomOccupancyDialogComponent } from './room-occupancy-dialog/room-occupancy-dialog.component';
import { UtsavNewPropertyDialogComponent } from '../establishment-utsavs/utsav-new-property-dialog/utsav-new-property-dialog.component';
import { sortTableRows } from '../../common-files/table-sort';

// properties of the branch with their rooms managed inline (expandable rows).
// Tabs: Mandir Only = the branch's properties (active by default, filterable);
// Utsav Only = properties assigned to the picked utsav.
@Component({
  selector: 'app-establishment-properties',
  standalone: true,
  imports: [CommonModule, MatSortModule, MatFormFieldModule, MatSelectModule, LoaderComponent],
  templateUrl: './establishment-properties.component.html',
  styleUrl: './establishment-properties.component.scss',
})
export class EstablishmentPropertiesComponent implements OnInit {
  allProperties: any[] = [];
  properties: any[] = [];
  filterUtsavList: any[] = [];
  activeTab: 'mandir' | 'utsav' = 'mandir';
  statusFilter: 'all' | 'active' | 'inactive' = 'active';
  utsavFilter: number | 'all' = 'all'; // Mandir tab: "assigned to utsav" filter
  unassignedOnly: boolean = false; // Mandir tab: "not assigned to any utsav"
  selectedUtsavId: number | null = null; // Utsav tab picker
  utsavAssignedRoomIds = new Set<number>(); // roomIds assigned to the picked utsav
  expandedPropertyId: number | null = null;
  roomsByPropertyId: { [propertyId: number]: any[] } = {};
  isLoading: boolean = false;
  // read-only for the Operator; add/edit/status for Admin+ (can_manage on 'properties').
  canManage: boolean = false;
  private sortState: Sort = { active: '', direction: '' };

  constructor(
    private establishmentService: EstablishmentService,
    private toastService: ToastService,
    private permissionService: PermissionService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.canManage = this.permissionService.can('properties', 'can_manage');
    this.loadUtsavs();
    this.load();
  }

  loadUtsavs(): void {
    this.establishmentService.listUtsavs().subscribe({
      next: (res: any) => {
        this.filterUtsavList = Array.isArray(res?.data?.utsavs) ? res.data.utsavs : [];
      },
    });
  }

  // the single reload path: refetch the master list, drop the rooms cache,
  // refetch the expanded row's rooms and (on the Utsav tab) the assignments.
  load(): void {
    this.isLoading = true;
    this.establishmentService.listProperties().subscribe({
      next: (res: any) => {
        this.allProperties = res?.data?.properties || [];
        this.roomsByPropertyId = {};
        if (this.expandedPropertyId !== null) this.loadRoomsFor(this.expandedPropertyId);
        if (this.activeTab === 'utsav' && this.selectedUtsavId) this.loadUtsavAssignments();
        this.applyFilters();
        this.isLoading = false;
      },
      error: (err) => {
        this.allProperties = [];
        this.properties = [];
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to load properties.');
      },
    });
  }

  setTab(tab: 'mandir' | 'utsav'): void {
    this.activeTab = tab;
    this.expandedPropertyId = null;
    if (tab === 'mandir') {
      this.statusFilter = 'active'; // Mandir = the branch's active properties
      this.unassignedOnly = false;
      this.utsavFilter = 'all';
    } else {
      this.statusFilter = 'all';
    }
    if (tab === 'utsav' && this.selectedUtsavId) this.loadUtsavAssignments();
    this.applyFilters();
  }

  onUtsavPicked(): void {
    this.expandedPropertyId = null;
    this.loadUtsavAssignments();
  }

  loadUtsavAssignments(): void {
    if (!this.selectedUtsavId) return;
    this.establishmentService.getUtsavAssignments(this.selectedUtsavId).subscribe({
      next: (res: any) => {
        const rooms = res?.data?.rooms || [];
        this.utsavAssignedRoomIds = new Set(rooms.map((r: any) => Number(r.roomId)));
        this.applyFilters();
      },
      error: (err) => {
        this.utsavAssignedRoomIds = new Set();
        this.toastService.error(err?.error?.message || 'Failed to load utsav assignments.');
      },
    });
  }

  applyFilters(): void {
    let filtered = [...this.allProperties];

    if (this.statusFilter === 'active') {
      filtered = filtered.filter((p) => this.isActive(p.status));
    } else if (this.statusFilter === 'inactive') {
      filtered = filtered.filter((p) => !this.isActive(p.status));
    }

    if (this.activeTab === 'mandir') {
      if (this.unassignedOnly) {
        filtered = filtered.filter((p) => !p.utsavs || p.utsavs.length === 0);
      } else if (this.utsavFilter !== 'all') {
        const targetUtsavId = Number(this.utsavFilter);
        filtered = filtered.filter(
          (p) => p.utsavs && p.utsavs.some((u: any) => Number(u.utsavId) === targetUtsavId)
        );
      }
    } else if (this.activeTab === 'utsav') {
      if (!this.selectedUtsavId) {
        filtered = [];
      } else {
        const utsavId = Number(this.selectedUtsavId);
        filtered = filtered.filter(
          (p) => p.utsavs && p.utsavs.some((u: any) => Number(u.utsavId) === utsavId)
        );
      }
    }

    this.properties = this.applySort(filtered);
  }

  sortProperties(sort: Sort): void {
    this.sortState = sort;
    this.applyFilters();
  }

  private applySort(properties: any[]): any[] {
    return sortTableRows(properties, this.sortState, {
      status: (property) => (Number(property.status) === 1 ? 'Active' : 'Inactive'),
    });
  }

  isActive(status: unknown): boolean {
    return Number(status) === 1;
  }

  // -- rooms (expandable row) --

  toggleExpand(property: any): void {
    const propertyId = Number(property.propertyId);
    if (this.expandedPropertyId === propertyId) {
      this.expandedPropertyId = null;
      return;
    }
    this.expandedPropertyId = propertyId;
    if (!this.roomsLoaded(propertyId)) this.loadRoomsFor(propertyId);
  }

  loadRoomsFor(propertyId: number): void {
    this.establishmentService.listRooms(propertyId).subscribe({
      next: (res: any) => {
        this.roomsByPropertyId[propertyId] = res?.data?.rooms || [];
      },
      error: (err) => {
        this.roomsByPropertyId[propertyId] = [];
        this.toastService.error(err?.error?.message || 'Failed to load rooms.');
      },
    });
  }

  roomsLoaded(propertyId: number): boolean {
    return this.roomsByPropertyId[propertyId] !== undefined;
  }

  // Utsav tab shows only the rooms assigned to the picked utsav.
  roomsForProperty(propertyId: number): any[] {
    const rooms = this.roomsByPropertyId[propertyId] || [];
    if (this.activeTab === 'utsav') {
      return rooms.filter((r: any) => this.utsavAssignedRoomIds.has(Number(r.roomId)));
    }
    return rooms;
  }

  openRoomForm(property: any, room: any | null): void {
    const ref = this.dialog.open(RoomFormDialogComponent, {
      width: '560px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: { room, propertyId: Number(property.propertyId) },
    });
    ref.afterClosed().subscribe((changed: boolean) => {
      if (changed) this.load();
    });
  }

  toggleRoomStatus(room: any): void {
    const nextStatus = this.isActive(room.status) ? 0 : 1;
    this.isLoading = true;
    this.establishmentService.setRoomStatus(Number(room.roomId), nextStatus).subscribe({
      next: (res: any) => {
        this.toastService.success(res?.message || 'Room status updated.');
        this.load();
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to update room status.');
      },
    });
  }

  // -- room assigned-status calendar --

  // per-date occupancy of the room for one utsav. On the Utsav tab the picked
  // utsav is used; on the Mandir tab the admin picks one of the ROOM's utsavs.
  assignedStatus(property: any, room: any): void {
    if (this.activeTab === 'utsav' && this.selectedUtsavId) {
      this.openOccupancyDialog(room, Number(this.selectedUtsavId));
      return;
    }
    this.pickFromUtsavs(
      room.utsavs || [],
      `Room ${room.roomNumber} — select utsav`,
      (utsavId) => this.openOccupancyDialog(room, utsavId),
      true
    );
  }

  private openOccupancyDialog(room: any, utsavId: number): void {
    this.dialog.open(RoomOccupancyDialogComponent, {
      width: '560px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: { roomId: Number(room.roomId), roomNumber: room.roomNumber, utsavId },
    });
  }

  // remove a room from an utsav from the Mandir tab: the dropdown lists the
  // utsavs the ROOM is assigned to (it can be on several).
  removeRoomFromUtsavMandir(room: any): void {
    this.pickFromUtsavs(
      room.utsavs || [],
      `Remove room ${room.roomNumber} from utsav`,
      (utsavId) => {
        this.isLoading = true;
        this.establishmentService.unassignRoom(utsavId, Number(room.roomId)).subscribe({
          next: (res: any) => {
            this.toastService.success(res?.message || 'Room removed from the utsav.');
            this.load();
          },
          error: (err) => {
            this.isLoading = false;
            this.toastService.error(err?.error?.message || 'Failed to remove room from the utsav.');
          },
        });
      },
      false
    );
  }

  // assign one room to an utsav from the Mandir tab: the dropdown lists the
  // branch's utsavs the room is not already in — inactive (not yet activated)
  // utsavs too, since rooms are assigned while the utsav is being prepared.
  assignRoomToUtsav(room: any): void {
    const assignedIds = (room.utsavs || []).map((u: any) => Number(u.utsavId));
    const candidates = this.filterUtsavList.filter(
      (u: any) => !assignedIds.includes(Number(u.utsavId))
    );
    this.pickFromUtsavs(
      candidates,
      `Assign room ${room.roomNumber} to utsav`,
      (utsavId) => {
        this.isLoading = true;
        this.establishmentService.addRoomsToUtsav(utsavId, [Number(room.roomId)]).subscribe({
          next: (res: any) => {
            this.toastService.success(res?.message || 'Room assigned to the utsav.');
            this.load();
          },
          error: (err) => {
            this.isLoading = false;
            this.toastService.error(err?.error?.message || 'Failed to assign room to the utsav.');
          },
        });
      },
      false
    );
  }

  // pick an utsav from the given options; skipWhenSingle jumps straight to the
  // only option (used for read-only views like the occupancy calendar).
  private pickFromUtsavs(
    utsavs: any[],
    title: string,
    onPicked: (utsavId: number) => void,
    skipWhenSingle: boolean
  ): void {
    if (!utsavs.length) {
      this.toastService.error('No utsavs available for this room.');
      return;
    }
    if (skipWhenSingle && utsavs.length === 1) {
      onPicked(Number(utsavs[0].utsavId));
      return;
    }
    const ref = this.dialog.open(SelectUtsavDialogComponent, {
      width: '420px',
      maxWidth: '95vw',
      data: { title, utsavs },
    });
    ref.afterClosed().subscribe((utsavId: number | undefined) => {
      if (utsavId) onPicked(Number(utsavId));
    });
  }

  // -- utsav assignment --

  openAssignDialog(property: any): void {
    const ref = this.dialog.open(AssignUtsavDialogComponent, {
      width: '560px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: { property },
    });
    ref.afterClosed().subscribe((changed: boolean) => {
      if (changed) this.load();
    });
  }

  addPropertyToUtsav(): void {
    if (!this.selectedUtsavId) return;
    const ref = this.dialog.open(UtsavNewPropertyDialogComponent, {
      width: '800px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: { utsavId: this.selectedUtsavId },
    });
    ref.afterClosed().subscribe((changed: boolean) => {
      if (changed) this.load();
    });
  }

  unassignPropertyFromUtsav(property: any): void {
    if (!this.selectedUtsavId) return;
    this.isLoading = true;
    this.establishmentService
      .unassignProperty(this.selectedUtsavId, Number(property.propertyId))
      .subscribe({
        next: (res: any) => {
          this.toastService.success(res?.message || 'Property removed from the utsav.');
          this.load();
        },
        error: (err) => {
          this.isLoading = false;
          this.toastService.error(err?.error?.message || 'Failed to remove property from the utsav.');
        },
      });
  }

  unassignRoomFromUtsav(room: any): void {
    if (!this.selectedUtsavId) return;
    this.isLoading = true;
    this.establishmentService.unassignRoom(this.selectedUtsavId, Number(room.roomId)).subscribe({
      next: (res: any) => {
        this.toastService.success(res?.message || 'Room removed from the utsav.');
        this.load();
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to remove room from the utsav.');
      },
    });
  }

  // -- property actions --

  openForm(property: any | null): void {
    const ref = this.dialog.open(PropertyFormDialogComponent, {
      width: '640px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: { property },
    });
    ref.afterClosed().subscribe((changed: boolean) => {
      if (changed) this.load();
    });
  }

  // inactivating a property also inactivates all its rooms (server cascade).
  togglePropertyStatus(property: any): void {
    const nextStatus = this.isActive(property.status) ? 0 : 1;
    this.isLoading = true;
    this.establishmentService.setPropertyStatus(Number(property.propertyId), nextStatus).subscribe({
      next: (res: any) => {
        this.toastService.success(res?.message || 'Status updated.');
        this.load();
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to update status.');
      },
    });
  }
}
